import React, { useState } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  TextField,
  Button,
  Chip,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Stack,
  useTheme,
  useMediaQuery,
  TablePagination, // ✅ Added
} from "@mui/material";
import { Link } from "react-router-dom";
import { Modal, message as AntMessage } from "antd";
import { Search } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// Static invoice data
const staticInvoices = [
  {
    _id: "1",
    date: "2024-01-15",
    buyer: "Rajesh Kumar",
    type: "Invoice",
    details: "Order #ORD-2024-001 - Smartphone, Earbuds",
    debit: 25000,
    credit: 0,
    balance: 25000,
  },
  {
    _id: "2",
    date: "2024-01-18",
    buyer: "Priya Sharma",
    type: "Invoice",
    details: "Order #ORD-2024-002 - Dress, Handbag",
    debit: 3500,
    credit: 0,
    balance: 3500,
  },
  {
    _id: "3",
    date: "2024-01-20",
    buyer: "Amit Patel",
    type: "Payment",
    details: "Payment received for Order #ORD-2024-003",
    debit: 0,
    credit: 1200,
    balance: -1200,
  },
  {
    _id: "4",
    date: "2024-01-22",
    buyer: "Sneha Desai",
    type: "Invoice",
    details: "Order #ORD-2024-004 - Wall Art, Cushions",
    debit: 4800,
    credit: 0,
    balance: 4800,
  },
  {
    _id: "5",
    date: "2024-01-25",
    buyer: "Vikram Singh",
    type: "Invoice",
    details: "Order #ORD-2024-005 - Cricket Bat, Ball",
    debit: 5600,
    credit: 0,
    balance: 5600,
  },
  {
    _id: "6",
    date: "2024-01-28",
    buyer: "Ananya Reddy",
    type: "Payment",
    details: "Payment received for Order #ORD-2024-006",
    debit: 0,
    credit: 2800,
    balance: -2800,
  },
  {
    _id: "7",
    date: "2024-02-01",
    buyer: "Karan Malhotra",
    type: "Invoice",
    details: "Order #ORD-2024-007 - Laptop, Mouse",
    debit: 55000,
    credit: 0,
    balance: 55000,
  },
  {
    _id: "8",
    date: "2024-02-03",
    buyer: "Meera Joshi",
    type: "Invoice",
    details: "Order #ORD-2024-008 - Monthly Groceries",
    debit: 8500,
    credit: 0,
    balance: 8500,
  },
  {
    _id: "9",
    date: "2024-02-05",
    buyer: "Rajesh Kumar",
    type: "Payment",
    details: "Partial payment for Order #ORD-2024-001",
    debit: 0,
    credit: 15000,
    balance: -15000,
  },
  {
    _id: "10",
    date: "2024-02-07",
    buyer: "Priya Sharma",
    type: "Invoice",
    details: "Order #ORD-2024-010 - Shoes, Accessories",
    debit: 6200,
    credit: 0,
    balance: 6200,
  },
];

function AdminPendingInvoices() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchTerm, setSearchTerm] = useState("");
  const [invoices, setInvoices] = useState(staticInvoices);
  const [selectedBuyer, setSelectedBuyer] = useState("All Buyers");
  const [page, setPage] = useState(0); // Added
  const [rowsPerPage, setRowsPerPage] = useState(5); //  Added

  // Added pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get unique buyers list
  const buyersList = [
    "All Buyers",
    ...new Set(invoices.map((inv) => inv.buyer)),
  ];

  // Filter data by selected buyer and search term
  const filteredInvoices = invoices?.filter((invoice) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (invoice.buyer && invoice.buyer.toLowerCase().includes(term)) ||
      (invoice.type && invoice.type.toLowerCase().includes(term)) ||
      (invoice.details && invoice.details.toLowerCase().includes(term)) ||
      (invoice.date && invoice.date.toLowerCase().includes(term));

    const matchesBuyer =
      selectedBuyer === "All Buyers" || invoice.buyer === selectedBuyer;

    return matchesSearch && matchesBuyer;
  });

  // Added pagination slice
  const paginatedInvoices = filteredInvoices?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate summary based on filtered data
  const totalReceivables = filteredInvoices?.reduce(
    (acc, inv) => acc + (inv.debit || 0) - (inv.credit || 0),
    0
  );
  const totalPayables = 0;
  const netBalance = totalReceivables - totalPayables;

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this invoice?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        setInvoices(invoices.filter((invoice) => invoice._id !== id));
        AntMessage.success("Invoice deleted successfully");
      },
    });
  };

  const getTypeColor = (type) => (type === "Payment" ? "success" : "default");

  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    const title = "Pending Invoices Report";
    const currentDate = new Date().toLocaleString();

    doc.setFontSize(16);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${currentDate}`, 14, 22);

    const tableColumn = [
      "#",
      "Date",
      "Buyer",
      "Type",
      "Details",
      "Debit (₹)",
      "Credit (₹)",
      "Balance",
    ];

    const tableRows = [];

    filteredInvoices.forEach((invoice, index) => {
      const invoiceData = [
        index + 1,
        invoice.date || "-",
        invoice.buyer || "-",
        invoice.type || "-",
        invoice.details || "-",
        invoice.debit ? `₹${invoice.debit.toLocaleString()}` : "-",
        invoice.credit ? `₹${invoice.credit.toLocaleString()}` : "-",
        invoice.balance ? `₹${invoice.balance.toLocaleString()}` : "-",
      ];
      tableRows.push(invoiceData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
      margin: { top: 30 },
    });

    const fileName = `Pending_Invoices_${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;
    doc.save(fileName);
  };

  return (
    <Box>
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Pending Invoices
      </Typography>

      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "15px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          width: "fit-content",
        }}
      >
        <MUILink
          component={Link}
          to="/admin-dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>
          Pending Invoices
        </Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by buyer, type, details, or date"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton>
                <Search sx={{ color: "primary.main" }} />
              </IconButton>
            ),
          }}
          sx={{
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Select
            value={selectedBuyer}
            onChange={(e) => setSelectedBuyer(e.target.value)}
            size="small"
            variant="outlined"
            sx={{
              minWidth: 150,
              bgcolor: "#fafbfc",
              fontWeight: 500,
              fontSize: 15,
              height: 36,
              borderRadius: "8px",
            }}
            IconComponent={ArrowDropDownIcon}
          >
            {buyersList?.map((buyer) => (
              <MenuItem key={buyer} value={buyer}>
                {buyer}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: "#0b4064",
              "&:hover": { bgcolor: "#09324d" },
              boxShadow: "none",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              height: 36,
            }}
            startIcon={<AddCircleOutlineIcon />}
          >
            New Entry
          </Button>
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadPDF}
          sx={{
            height: 36,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Export
        </Button>
      </Box>

      {/* Table for Desktop */}
      {!isMobile ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>
                  <strong>#</strong>
                </TableCell>
                <TableCell>
                  <strong>Date</strong>
                </TableCell>
                <TableCell>
                  <strong>Buyer</strong>
                </TableCell>
                <TableCell>
                  <strong>Type</strong>
                </TableCell>
                <TableCell>
                  <strong>Details</strong>
                </TableCell>
                <TableCell>
                  <strong>Debit (₹)</strong>
                </TableCell>
                <TableCell>
                  <strong>Credit (₹)</strong>
                </TableCell>
                <TableCell>
                  <strong>Balance</strong>
                </TableCell>
                <TableCell>
                  <strong>Action</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Changed to paginatedInvoices and updated index */}
              {paginatedInvoices?.map((invoice, index) => (
                <TableRow key={invoice._id}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{invoice.date || "-"}</TableCell>
                  <TableCell>{invoice.buyer || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.type}
                      color={getTypeColor(invoice.type)}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: 13 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14 }}>
                      {invoice.details || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {invoice.debit
                      ? `${invoice.debit.toLocaleString()}.00`
                      : "-"}
                  </TableCell>
                  <TableCell
                    sx={{ color: invoice.credit ? "#43a047" : undefined }}
                  >
                    {invoice.credit
                      ? `${invoice.credit.toLocaleString()}.00`
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {invoice.balance
                      ? `${invoice.balance.toLocaleString()}.00`
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDelete(invoice._id)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredInvoices?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No invoices found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/*  Added TablePagination */}
          <TablePagination
            component="div"
            count={filteredInvoices?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      ) : (
        // Card View for Mobile
        <Box sx={{ mb: 3 }}>
          <Stack spacing={2}>
            {/* Changed to paginatedInvoices for mobile view */}
            {paginatedInvoices?.map((invoice, index) => (
              <Card
                key={invoice._id}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 14, color: "#7a7a7a" }}>
                        Date
                      </Typography>
                      <Typography fontWeight={600}>
                        {invoice.date || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 14, color: "#7a7a7a" }}>
                        Buyer
                      </Typography>
                      <Typography fontWeight={600}>
                        {invoice.buyer || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 14, color: "#7a7a7a" }}>
                        Type
                      </Typography>
                      <Chip
                        label={invoice.type}
                        color={getTypeColor(invoice.type)}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: 13 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: 14, color: "#7a7a7a" }}>
                        Details
                      </Typography>
                      <Typography>{invoice.details || "-"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 14, color: "#7a7a7a" }}>
                        Debit (₹)
                      </Typography>
                      <Typography>
                        {invoice.debit
                          ? `${invoice.debit.toLocaleString()}.00`
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 14, color: "#7a7a7a" }}>
                        Credit (₹)
                      </Typography>
                      <Typography
                        sx={{ color: invoice.credit ? "#43a047" : undefined }}
                      >
                        {invoice.credit
                          ? `${invoice.credit.toLocaleString()}.00`
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: 14, color: "#7a7a7a" }}>
                        Balance
                      </Typography>
                      <Typography fontWeight={700}>
                        {invoice.balance
                          ? `${invoice.balance.toLocaleString()}.00`
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton onClick={() => handleDelete(invoice._id)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/*  Added pagination for mobile view */}
          <TablePagination
            component="div"
            count={filteredInvoices?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ mt: 2 }}
          />
        </Box>
      )}

      {/* Summary Footer */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          background: "#f6f8fa",
          p: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mt: 3,
        }}
      >
        <Typography sx={{ fontWeight: 600, color: "#444", fontSize: 16 }}>
          Total Receivables <br />
          <span style={{ fontWeight: 900, fontSize: 22, color: "#222" }}>
            ₹
            {totalReceivables?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </span>
        </Typography>
        <Typography sx={{ fontWeight: 600, color: "#444", fontSize: 16 }}>
          Total Payables <br />
          <span style={{ fontWeight: 900, fontSize: 22, color: "#222" }}>
            ₹
            {totalPayables?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </span>
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            color: "#368f3d",
            fontSize: 16,
            textAlign: { xs: "left", sm: "right" },
          }}
        >
          Net Balance <br />
          <span style={{ fontWeight: 900, fontSize: 22, color: "#23b054" }}>
            ₹
            {netBalance?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}{" "}
            (Receivable)
          </span>
        </Typography>
      </Paper>
    </Box>
  );
}

export default AdminPendingInvoices;